import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, Loader2, Wrench, Trash2, Mic, Square, Sparkles, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthToken } from '../../utils/auth';
import { transcribeAudio } from '../../utils/voiceService';
import Layout from '../../components/Layout';
import IdeaProjects from './IdeaProjects';
import { PipelineBoard } from '../../components/PipelineBoard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

const STAGES = ['Idea', 'Plan', 'People', 'Ready'];

const ProgressDots = ({ stage }) => (
  <div className="flex items-center gap-0 mt-3 mb-1" data-testid="progress-dots">
    {STAGES.map((label, i) => {
      const n = i + 1;
      const done = n <= (stage || 1);
      return (
        <React.Fragment key={label}>
          {i > 0 && <div className={`h-[2px] flex-1 min-w-[16px] ${n <= stage ? 'bg-amber-400' : 'bg-white/10'}`} />}
          <div className="flex flex-col items-center gap-1">
            <div className={`w-4 h-4 rounded-full border-2 ${done ? 'bg-amber-400 border-amber-400' : 'bg-transparent border-white/20'}`} />
            <span className={`text-[10px] font-bold ${done ? 'text-amber-300' : 'text-zinc-600'}`}>{label}</span>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

const WORKING_LINES = [
  'Reading your idea…',
  'Sketching the plan…',
  'Pricing the parts…',
  'Finding who can build it…',
  'Checking what could go wrong…',
];

const WorkingTicker = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % WORKING_LINES.length), 1800);
    return () => clearInterval(t);
  }, []);
  return (
    <p className="text-[13px] text-amber-300 mt-3 flex items-center gap-2 magic-in" data-testid="working-ticker">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> {WORKING_LINES[i]}
    </p>
  );
};

export default function InventionsList() {
  const [creating, setCreating] = useState(false);
  const [idea, setIdea] = useState('');
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = React.useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const [board, setBoard] = useState([]);
  const navigate = useNavigate();

  const loadBoard = () => {
    axios.get(`${API_URL}/api/idea/pipeline/board`, auth())
      .then((r) => setBoard(r.data.projects || []))
      .catch(() => {});
  };
  useEffect(loadBoard, [refreshKey]);

  const newIdea = async (text) => {
    const desc = (typeof text === 'string' ? text : idea).trim();
    if (desc.length < 10) {
      toast.error('Tell LEE a bit more about the idea');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/idea/projects`,
        { description: desc }, auth());
      toast.success('LEE is on it — sit back, the plan is coming (2–4 min)');
      setIdea('');
      setRefreshKey((k) => k + 1);
    } catch (_e) {
      toast.error('Failed to start');
    } finally {
      setCreating(false);
    }
  };

  const toggleRecord = async () => {
    if (recording) {
      mediaRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setTranscribing(true);
        try {
          const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
          const text = (await transcribeAudio(blob) || '').trim();
          if (text.length >= 10) {
            setIdea(text);
            toast.success(`LEE heard: "${text.slice(0, 80)}${text.length > 80 ? '…' : ''}" — starting the plan`);
            await newIdea(text);
          } else if (text) {
            setIdea(text);
            toast.error('That was short — add a bit more, then tap the big button');
          } else {
            toast.error("LEE couldn't hear that — try again closer to the mic");
          }
        } catch (e) {
          toast.error('Transcription failed — try again');
        }
        setTranscribing(false);
      };
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
      toast('Listening… tell LEE the idea, tap the square when done');
    } catch (e) {
      toast.error('Microphone blocked — allow mic access for LEE');
    }
  };

  const deleteIdea = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This removes its plan and unsent drafts. This can't be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/idea/projects/${p.id}`, auth());
      toast.success('Idea deleted');
      setRefreshKey((k) => k + 1);
    } catch (e) { toast.error('Could not delete'); }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-8" data-testid="inventions-page">
        {/* Giant friendly ask */}
        <div className="text-center pt-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-500/15 border border-amber-500/30 mb-3">
            <Lightbulb className="h-7 w-7 text-amber-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">What's your idea?</h1>
          <p className="text-zinc-500 mt-2 text-sm max-w-sm mx-auto">
            Say it or type it. LEE plans it, prices it and finds who builds it — you just say yes.
          </p>
        </div>

        {/* One big box */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 shadow-[0_20px_60px_-30px_rgba(245,158,11,0.15)]" data-testid="idea-composer">
          <textarea
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            rows={3}
            placeholder="A smart jar… a food truck… an app… anything at all."
            className="w-full bg-transparent text-lg text-white placeholder-zinc-600 outline-none resize-none"
            data-testid="idea-input"
          />
          <div className="flex items-center gap-3 mt-2">
            <button onClick={toggleRecord} disabled={transcribing || creating}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 shrink-0 ${recording ? 'bg-red-500 text-white animate-pulse' : 'bg-white/[0.06] text-zinc-400 hover:text-amber-300'}`}
              title={recording ? 'Finish recording' : 'Speak the idea'}
              data-testid="idea-mic-btn">
              {transcribing ? <Loader2 className="h-5 w-5 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-5 w-5" />}
            </button>
            {recording && <span className="text-[12px] text-red-400 font-bold" data-testid="recording-hint">listening…</span>}
            {transcribing && <span className="text-[12px] text-amber-400 font-bold">LEE is writing it down…</span>}
            <button
              data-testid="new-venture-btn"
              onClick={() => newIdea()}
              disabled={creating || recording || transcribing}
              className="ml-auto bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 rounded-full font-bold text-[15px] flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {creating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
              {creating ? 'Starting…' : 'Make it happen'}
            </button>
          </div>
          {creating && <WorkingTicker />}
        </div>

        {/* Picture-book idea cards: progress dots + one plain line + ONE button */}
        <div className="space-y-4" data-testid="simple-ideas-list">
          {board.map((p, cardIdx) => (
            <div key={p.id} className="magic-in rounded-3xl border border-white/10 bg-white/[0.03] p-5" style={{ animationDelay: `${cardIdx * 90}ms` }} data-testid="simple-idea-card">
              <div className="flex items-start justify-between gap-2">
                <p className="text-[17px] font-bold text-white leading-snug">{p.title}</p>
                <button onClick={() => deleteIdea(p)}
                  className="text-zinc-700 hover:text-red-400 transition-colors p-1 shrink-0"
                  title="Delete this idea" data-testid="simple-delete-btn">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <ProgressDots stage={p.simple_stage} />
              <p className="text-[15px] text-zinc-300 mt-2 leading-snug" data-testid="simple-next-line">
                {p.simple_line || p.next_action}
              </p>
              <div className="flex items-center gap-4 mt-4">
                <button onClick={() => navigate(`/inventions/${p.id}/builders`)}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-6 py-3 rounded-full bg-amber-500 text-black text-[15px] font-bold hover:bg-amber-400 transition-colors"
                  data-testid="simple-bring-to-life-btn">
                  {p.action_label || 'See the plan'} <ChevronRight className="w-4 h-4" />
                </button>
                <button onClick={() => { setAdvanced(true); setSelectedId(p.id); }}
                  className="text-[13px] text-zinc-600 hover:text-zinc-300 transition-colors"
                  data-testid="simple-details-btn">
                  details
                </button>
              </div>
            </div>
          ))}
          {board.length === 0 && (
            <p className="text-sm text-zinc-500 text-center py-6">No ideas yet — say one above and LEE takes it from there.</p>
          )}
        </div>

        {/* Advanced pipeline tucked away */}
        <button onClick={() => setAdvanced(!advanced)}
          className="w-full flex items-center justify-center gap-1.5 text-[12px] text-zinc-600 hover:text-zinc-400 transition-colors py-1"
          data-testid="advanced-toggle-btn">
          <Wrench className="w-3.5 h-3.5" />
          {advanced ? 'Hide the engine room' : 'Engine room (parts, RFQs, scorecards)'}
        </button>

        {advanced && (
          <div className="space-y-6" data-testid="advanced-panel">
            <PipelineBoard onSelect={setSelectedId} />
            <IdeaProjects key={refreshKey} expandId={selectedId} />
          </div>
        )}
      </div>
    </Layout>
  );
}
